import type { StyledComponent } from '@emotion/styled';
import styled from '@emotion/styled';
import { Prismic, PrismicLink, PrismicContext } from '@landsbankinn/prismic';
import {
  device,
  Divider,
  mobileGutterMargin,
  mobileGutterPadding,
  typography,
  shouldForwardProp,
} from '@landsbankinn/ui';
import { useRouter } from 'next/router';
import React, {
  forwardRef,
  useState,
  Children,
  cloneElement,
  useContext,
} from 'react';
import type { FC } from 'react';
import { useSpring, animated } from 'react-spring';
import { Img } from '../../components/img/Img';
import type { ImgSource, ImgAr } from '../../components/img/Img';

export const CardHostLink: StyledComponent<any> = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})`
  display: block;
  position: relative;
  width: 100%;
  cursor: pointer;

  margin-bottom: 4em;

  &:last-of-type {
    margin-bottom: 2em;
  }

  @media ${device.tablet} {
    margin-bottom: 0;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const ImageLink = styled(CardHostLink, {
  shouldForwardProp: shouldForwardProp('div'),
})`
  margin-bottom: 36px;

  @media ${device.tablet} {
    margin-bottom: 0;
  }

  &:after {
    content: '';
    height: 20px;
    width: 100%;

    position: absolute;
    bottom: -20px;
    left: 0;
    right: 0;
  }
`;

const Overflow = styled.div`
  height: 100%;
  margin: ${(props) => `0 -${props.theme.containerGutter}px`};
  overflow: hidden;

  ${mobileGutterMargin}

  @media ${device.tablet} {
    margin: 0;
  }
`;

const ImgWrap = styled(animated.div)`
  height: 100%;
  transform: scale(1);

  > *:first-child {
    height: 100%;
  }
`;

const Title = styled.h2`
  ${typography.h3};
  margin-bottom: 3px;
`;

const Copy = styled.div<{ isFeatured?: boolean }>`
  ${typography.body}
  margin-bottom: 24px;

  @media ${device.tablet} {
    padding-right: 28px;
  }

  @media ${device.desktop} {
    padding-right: ${(props) => (props.isFeatured ? '200px' : '28px')};
  }
`;

const ActionSpace = styled.div`
  position: relative;
  opacity: 0;
  pointer-events: none;
`;

const ActionPosition = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

interface CardActionProps {
  children: React.ReactElement;
  hover?: boolean;
}

export const CardAction = ({ children, hover, ...rest }: CardActionProps) => {
  return (
    <>
      <ActionSpace>{children}</ActionSpace>
      <ActionPosition>
        {cloneElement(
          Children.only(children),
          { hover: hover, ...rest },
          { ...children },
        )}
      </ActionPosition>
    </>
  );
};

export interface CardProps {
  link?: Prismic.Link;
  title: string | null;
  description: React.ReactNode;
  image?: ImgSource;
  ar?: ImgAr;
  imageAspect?: number;
  action?: React.ReactElement;
  padding?: boolean;
  backgroundAlt?: boolean;
  imageRef?: any;
  ref?: any;
  notLink?: boolean;
  isFeatured?: boolean;
  onMouseEnter?: any;
  onMouseLeave?: any;
  onClick?: any;
  [key: string]: any;
}

export const SplittedCard: FC<CardProps> = forwardRef<
  HTMLDivElement,
  CardProps
>(function CardFn(props: CardProps, ref) {
  const {
    link,
    title,
    description,
    image,
    ar = 'ar_16_9',
    action,
    imageAspect,
    imageRef,
    notLink,
    isFeatured,
    onMouseEnter,
    onMouseLeave,
    ...rest
  } = props;
  const router = useRouter();
  const { urlResolver } = useContext(PrismicContext);
  const url = Prismic.link(urlResolver, link);

  const [hover, setHover] = useState(false);

  const onHover = useSpring({
    transform: hover ? 'scale(1.025)' : 'scale(1)',
  });

  const onEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const onLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window?.getSelection?.()?.type === 'Range') {
      e.preventDefault();
      return false;
    }
    return router.push(url.href ?? '', url.as);
  };

  return (
    <>
      <ImageLink
        ref={ref}
        {...rest}
        onMouseLeave={onLeave}
        onMouseEnter={onEnter}
        onClick={onClick}
      >
        <Overflow>
          <ImgWrap style={{ ...onHover }}>
            <Img
              src={image}
              ar={ar}
              aspectRatio={imageAspect}
              ref={imageRef}
              percentage={isFeatured ? 0.86 : 0.33}
            />
          </ImgWrap>
        </Overflow>
      </ImageLink>
      <CardHostLink
        ref={ref}
        {...rest}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onClick}
      >
        {!!title && (
          <Title>
            <PrismicLink value={link} title={title}>
              <a>{title}</a>
            </PrismicLink>
          </Title>
        )}
        {!!title && <Divider />}
        <Copy isFeatured={isFeatured}>{description}</Copy>
        {/* {!!action && (
          <PrismicLink value={link} title={title}>
            <CardAction hover={hover}>{action}</CardAction>
          </PrismicLink>
        )} */}
      </CardHostLink>
    </>
  );
});
